import { useEffect, useState } from 'react';
import { backend_url } from '../../App';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AddReview = ({ onClose, productId }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        reviewTitle: '',
        review: '',
        rating: 0,
        showHide: false,
        reviewImage: null,
        productId: productId,
    });
    const [hover, setHover] = useState(0);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (productId) {
            setFormData((prev) => ({ ...prev, productId }));
        }
    }, [productId]);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (files) {
            const file = files[0];
            setFormData((prev) => ({ ...prev, [name]: file }));
            if (name === "reviewImage" && file) setPreview(URL.createObjectURL(file));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleStarClick = (rating) => {
        setFormData((prev) => ({ ...prev, rating }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.rating) {
            toast.error("Please select a star rating.");
            return;
        }
        const form = new FormData();
        for (const key in formData) {
            form.append(key, formData[key]);
        }

        try {
            const res = await fetch(`${backend_url}/api/customerreview`, {
                method: 'POST',
                body: form
            });
            if (res.ok) {
                toast.success("Review submitted successfully!");
                setTimeout(() => onClose(), 1500);
            } else {
                toast.error("Could not submit review. Please try again.");
            }
        } catch (err) {
            console.error('Error submitting review:', err);
            toast.error("Something went wrong.");
        }
    };

    const inputCls =
        "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[var(--brand-primary-500)]/30 focus:border-[var(--brand-primary-400)] outline-none transition";
    const labelCls = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5";

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center max-md:items-end z-[100] p-4 max-md:p-0">
            <div className="bg-white rounded-2xl max-md:rounded-b-none max-md:rounded-t-3xl shadow-2xl w-full max-w-md max-h-[90vh] max-md:max-h-[92vh] flex flex-col overflow-hidden">
                {/* Drag-handle affordance — mobile bottom-sheet only */}
                <div className="md:hidden flex justify-center pt-2.5 pb-1 flex-shrink-0">
                    <span className="w-10 h-1 rounded-full bg-gray-300" />
                </div>

                {/* Header */}
                <div className="relative p-5 flex-shrink-0 flex items-center justify-between gap-3"
                    style={{ background: "linear-gradient(100deg,#5B2B9E 0%,#7A4CC0 100%)" }}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white/10 ring-1 ring-white/20 flex items-center justify-center text-lg">✍️</div>
                        <div>
                            <h2 className="text-lg font-bold text-white leading-tight">Write a Review</h2>
                            <p className="text-[var(--brand-primary-100)] text-xs">Share your experience with our fabrics</p>
                        </div>
                    </div>
                    <button onClick={onClose} aria-label="Close"
                        className="text-white/80 hover:text-white hover:bg-white/15 rounded-full p-2 transition-colors flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
                    <div className="flex-1 overflow-y-auto p-5 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Name *</label>
                                <input type="text" name="name" placeholder="Your name" value={formData.name}
                                    onChange={handleChange} required autoComplete="off" className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Email *</label>
                                <input type="email" name="email" placeholder="you@example.com" value={formData.email}
                                    onChange={handleChange} required autoComplete="off" className={inputCls} />
                            </div>
                        </div>

                        <div>
                            <label className={labelCls}>Review Title *</label>
                            <input type="text" name="reviewTitle" placeholder="Sum it up in a few words" value={formData.reviewTitle}
                                onChange={handleChange} required autoComplete="off" className={inputCls} />
                        </div>

                        <div>
                            <label className={labelCls}>Your Review *</label>
                            <textarea name="review" placeholder="Tell us what you loved…" value={formData.review}
                                onChange={handleChange} required rows="4" maxLength={150} autoComplete="off"
                                className={`${inputCls} resize-none`} />
                            <p className="text-right text-[11px] text-gray-400 mt-1">{formData.review.length}/150</p>
                        </div>

                        {/* Star Rating */}
                        <div>
                            <label className={labelCls}>Your Rating *</label>
                            <div className="flex items-center gap-1.5">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button key={star} type="button"
                                        onMouseEnter={() => setHover(star)}
                                        onMouseLeave={() => setHover(0)}
                                        onClick={() => handleStarClick(star)}
                                        className="text-3xl leading-none transition-transform hover:scale-110 focus:outline-none"
                                        style={{ color: (hover || formData.rating) >= star ? "#F59E0B" : "#E5E7EB" }}
                                        aria-label={`${star} star`}>
                                        ★
                                    </button>
                                ))}
                                <span className="ml-2 text-xs font-medium text-gray-400">
                                    {formData.rating ? `${formData.rating}/5` : "Tap to rate"}
                                </span>
                            </div>
                        </div>

                        {/* Image upload */}
                        <div>
                            <label className={labelCls}>Add a Photo (optional)</label>
                            <label htmlFor="reviewImage"
                                className="flex items-center gap-3 border-2 border-dashed border-gray-200 rounded-lg px-3 py-3 cursor-pointer hover:border-[var(--brand-primary-300)] hover:bg-[var(--brand-primary-50)]/30 transition">
                                {preview ? (
                                    <img src={preview} alt="preview" className="w-12 h-12 rounded-lg object-cover border border-gray-200" />
                                ) : (
                                    <span className="w-12 h-12 rounded-lg bg-[var(--brand-primary-50)] flex items-center justify-center text-xl flex-shrink-0">📷</span>
                                )}
                                <span className="text-sm text-gray-500 truncate">
                                    {formData.reviewImage?.name || "Click to choose an image"}
                                </span>
                            </label>
                            <input id="reviewImage" type="file" name="reviewImage" onChange={handleChange} accept="image/*" className="hidden" />
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex-shrink-0 border-t border-gray-100 p-4 flex gap-3">
                        <button type="button" onClick={onClose}
                            className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors">
                            Cancel
                        </button>
                        <button type="submit"
                            className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold transition hover:brightness-110 active:scale-[0.99]"
                            style={{ background: "#6D3BB5" }}>
                            Submit Review
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddReview;
